import React from 'react';
import { Image } from 'lucide-react';

interface VideoThumbnail {
  url: string;
  cloudfrontUrl?: string;
  mediaId?: string;
  alt?: string;
}

interface VideoData {
  thumbnail?: VideoThumbnail;
  [key: string]: any;
}

interface VideoThumbnailSectionProps {
  data: VideoData;
  setFormData: (data: VideoData) => void;
  openThumbnailUpload: () => void;
}

export const VideoThumbnailSection: React.FC<VideoThumbnailSectionProps> = ({
  data,
  setFormData,
  openThumbnailUpload,
}) => {
  const handleRemoveThumbnail = () => {
    setFormData({
      ...data,
      thumbnail: {
        ...data.thumbnail,
        url: '',
        cloudfrontUrl: undefined,
        mediaId: undefined,
      },
    });
  };

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">Video Thumbnail</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Thumbnail Image</label>

          {data.thumbnail?.url ? (
            <div className="space-y-3">
              {/* Thumbnail Preview */}
              <div className="relative border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50 group inline-block">
                <img
                  src={data.thumbnail.cloudfrontUrl || data.thumbnail.url}
                  alt={data.thumbnail.alt || 'Video thumbnail'}
                  className="w-32 h-24 object-cover"
                />

                {/* Overlay with actions */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={openThumbnailUpload}
                      className="px-2 py-1 bg-white text-gray-700 rounded shadow text-xs"
                    >
                      Replace
                    </button>
                    <button
                      type="button"
                      onClick={handleRemoveThumbnail}
                      className="px-2 py-1 bg-red-600 text-white rounded shadow text-xs"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-400 hover:bg-primary-50 transition-all duration-200 cursor-pointer group"
              onClick={openThumbnailUpload}
            >
              <div className="flex flex-col items-center space-y-2">
                <div className="p-2 bg-gray-100 group-hover:bg-primary-100 rounded-full">
                  <Image size={24} className="text-gray-600 group-hover:text-primary-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Upload Thumbnail</p>
                  <p className="text-xs text-gray-500">PNG, JPG up to 10MB</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Alt Text</label>
          <input
            type="text"
            value={data.thumbnail?.alt || ''}
            onChange={e =>
              setFormData({
                ...data,
                thumbnail: {
                  url: data.thumbnail?.url || '',
                  ...data.thumbnail,
                  alt: e.target.value,
                },
              })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Describe the thumbnail image"
          />
        </div>
      </div>
    </div>
  );
};
